import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/features/settings/billing/hooks/use-billing";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";
import mixpanel from "mixpanel-browser";

export const BillingOverviewSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    billingData,
    isBillingLoading,
    handleEditPaymentMethod,
    handleUpgrade,
    handleUpgradeToTopup,
  } = useBilling();

  // Calculate credit usage percentage
  const creditUsagePercentage = useMemo(() => {
    if (!billingData?.data?.usage) return 0;
    const used = billingData.data.usage.tokens.subscription || 0;
    const max = billingData.data.usage.maxTokens.subscription || 1;
    return (used / max) * 100;
  }, [billingData]);

  // Calculate reset days from subscription data
  const resetDays = useMemo(() => {
    if (!billingData?.data?.subscription?.expiresAt) return 0;
    const expiresAt = new Date(billingData.data.subscription.expiresAt);
    const now = new Date();
    const diffTime = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [billingData]);

  // Calculate next charge date from subscription data
  const nextChargeDate = useMemo(() => {
    if (!billingData?.data?.subscription?.expiresAt) return null;
    const expiresAt = new Date(billingData.data.subscription.expiresAt);
    return expiresAt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [billingData]);

  // Get subscription status message
  const getSubscriptionStatusMessage = () => {
    const subscription = billingData?.data?.subscription;
    if (!subscription) return null;

    if (subscription.status === "canceled") {
      return t("settings.usage.subscriptionCanceled", {
        date: nextChargeDate || "N/A",
      });
    }

    if (subscription.status === "active") {
      return t("settings.usage.nextChargeOn", {
        date: nextChargeDate || "N/A",
      });
    }

    return null;
  };

  // Format expiry date
  const formatExpiryDate = (month?: number, year?: number) => {
    if (!month || !year) return "N/A";
    return `${String(month).padStart(2, "0")}/${year}`;
  };

  if (isBillingLoading) {
    return (
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
          {t("settings.usage.billingOverview")}
        </h2>
        <div className="h-32 bg-gray-50 dark:bg-gray-700 rounded-xl animate-pulse" />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
        {t("settings.usage.billingOverview")}
      </h2>

      {/* Desktop Layout */}
      <div className="hidden lg:flex gap-8 rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-4">
        {/* Plan Card */}
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-text-quaternary dark:text-gray-400">
                {t("settings.usage.plan")}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-col">
                <p className="text-base font-semibold text-text-primary dark:text-gray-100">
                  {billingData?.data?.pricingPlan?.name ||
                    t("settings.usage.basicPlan")}
                </p>
                <p className="text-sm font-medium text-text-secondary dark:text-gray-300">
                  {billingData?.data?.pricingPlan?.currency || "$"}
                  {billingData?.data?.pricingPlan?.originalPrice || 0}{" "}
                  {t("settings.usage.perMonth")}
                </p>
              </div>
            </div>
          </div>
          <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
            {getSubscriptionStatusMessage() || "N/A"}
          </p>
        </div>

        {/* Monthly Credits Card */}
        <div className="flex-1 flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.monthlyCredits")}
            </p>
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-text-primary dark:text-gray-100">
                {t("settings.usage.usedOfCredits", {
                  used: (
                    billingData?.data?.usage?.tokens?.subscription || 0
                  ).toLocaleString(),
                  total: (
                    billingData?.data?.usage?.maxTokens?.subscription || 0
                  ).toLocaleString(),
                })}
              </p>
              {/* Progress Bar */}
              <div className="w-[216px] h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 dark:bg-indigo-400 rounded-full transition-all duration-300"
                  style={{ width: `${creditUsagePercentage}%` }}
                />
              </div>
              <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
                {t("settings.usage.resetIn", {
                  days: resetDays,
                })}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              mixpanel.track("upgrade_plan_clicked");
              window.netmera.push((api) => {
                api.sendEvent({
                  code: "qcsfp",
                });
              });
              handleUpgrade();
            }}
            className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 transition-colors self-start"
          >
            {t("settings.usage.upgradePlan")}
          </button>
        </div>

        {/* Additional Credits Card */}
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.additionalCredits")}
            </p>
            <div className="flex flex-col gap-0.5">
              <p className="text-base font-semibold text-text-primary dark:text-gray-100">
                {t("settings.usage.credits", {
                  count: billingData?.data?.usage?.tokens?.oneTime || 0,
                })}
              </p>
            </div>
          </div>
          <button
            onClick={handleUpgradeToTopup}
            className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 transition-colors self-start"
          >
            {t("settings.usage.buyExtraCredits")}
          </button>
        </div>

        {/* Payment Method Card */}
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.paymentMethod")}
            </p>
            {billingData?.data?.paymentMethod ? (
              <div className="flex gap-3">
                <PaymentBrandLogo
                  brand={billingData.data.paymentMethod.brand}
                  className="w-6 h-6"
                />
                <div className="flex flex-col gap-0.5">
                  <p className="text-base font-semibold text-text-primary dark:text-gray-100">
                    ****{billingData.data.paymentMethod.last4}
                  </p>
                  <p className="text-sm font-medium text-text-secondary dark:text-gray-300">
                    {t("settings.usage.expiresDate", {
                      date: formatExpiryDate(
                        billingData.data.paymentMethod.exp_month,
                        billingData.data.paymentMethod.exp_year,
                      ),
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-text-tertiary dark:text-gray-400">
                No payment method added
              </p>
            )}
          </div>
          <button
            onClick={handleEditPaymentMethod}
            className="text-sm font-semibold text-indigo-500 hover:text-indigo-600 transition-colors self-start"
          >
            {t("settings.usage.managePayment")}
          </button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col gap-4">
        {/* Plan Card */}
        <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl p-4">
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.plan")}
            </p>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-text-primary dark:text-gray-100">
                {billingData?.data?.pricingPlan?.name ||
                  t("settings.usage.basicPlan")}
              </p>
              <p className="text-xs font-medium text-text-secondary dark:text-gray-300">
                {billingData?.data?.pricingPlan?.currency || "$"}
                {billingData?.data?.pricingPlan?.originalPrice || 0}{" "}
                {t("settings.usage.perMonth")}
              </p>
            </div>
            <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
              {getSubscriptionStatusMessage() || "N/A"}
            </p>
          </div>
        </div>

        {/* Monthly Credits Card */}
        <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl p-4">
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.monthlyCredits")}
            </p>
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-text-primary dark:text-gray-100">
                {t("settings.usage.usedOfCredits", {
                  used: (
                    billingData?.data?.usage?.tokens?.subscription || 0
                  ).toLocaleString(),
                  total: (
                    billingData?.data?.usage?.maxTokens?.subscription || 0
                  ).toLocaleString(),
                })}
              </p>
              {/* Progress Bar */}
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 dark:bg-indigo-400 rounded-full transition-all duration-300"
                  style={{ width: `${creditUsagePercentage}%` }}
                />
              </div>
              <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
                {t("settings.usage.resetIn", {
                  days: resetDays,
                })}
              </p>
            </div>
            <button
              onClick={() => {
                mixpanel.track("upgrade_plan_clicked");
                window.netmera.push((api) => {
                  api.sendEvent({
                    code: "qcsfp",
                  });
                });
                handleUpgrade();
              }}
              className="text-xs font-semibold text-indigo-500 hover:text-indigo-600 transition-colors self-start"
            >
              {t("settings.usage.upgradePlan")}
            </button>
          </div>
        </div>

        {/* Additional Credits Card */}
        <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl p-4">
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.additionalCredits")}
            </p>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-text-primary dark:text-gray-100">
                {t("settings.usage.credits", {
                  count: billingData?.data?.usage?.tokens?.oneTime || 0,
                })}
              </p>
            </div>
            <button
              onClick={handleUpgradeToTopup}
              className="text-xs font-semibold text-indigo-500 hover:text-indigo-600 transition-colors self-start"
            >
              {t("settings.usage.buyExtraCredits")}
            </button>
          </div>
        </div>

        {/* Payment Method Card */}
        <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl p-4">
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium text-text-quaternary dark:text-gray-400">
              {t("settings.usage.paymentMethod")}
            </p>
            {billingData?.data?.paymentMethod ? (
              <div className="flex gap-2">
                <PaymentBrandLogo
                  brand={billingData.data.paymentMethod.brand}
                  className="w-5 h-5"
                />
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-semibold text-text-primary dark:text-gray-100">
                    ****{billingData.data.paymentMethod.last4}
                  </p>
                  <p className="text-xs font-medium text-text-secondary dark:text-gray-300">
                    {t("settings.usage.expiresDate", {
                      date: formatExpiryDate(
                        billingData.data.paymentMethod.exp_month,
                        billingData.data.paymentMethod.exp_year,
                      ),
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-tertiary dark:text-gray-400">
                No payment method added
              </p>
            )}
            <button
              onClick={handleEditPaymentMethod}
              className="text-xs font-semibold text-indigo-500 hover:text-indigo-600 transition-colors self-start"
            >
              {t("settings.usage.managePayment")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
